const roundPrice = (value) => Math.round(Number(value) * 100) / 100;

/**
 * Catalog products: `price` is the original/list price; use `isSale` + `salePercent` or `salePrice`.
 * Cart items: `originalPrice` + `price` (effective) are set at add-to-cart time.
 */
export const getProductPricing = (product) => {
  if (!product) {
    return { originalPrice: 0, salePrice: null, effectivePrice: 0, isOnSale: false, salePercent: 0 };
  }

  const isSaleFlag = Boolean(product.isSale);
  const explicitOriginal =
    product.originalPrice != null ? Number(product.originalPrice) : null;
  const listedPrice = Number(product.price ?? product.product_price ?? 0) || 0;

  if (!isSaleFlag) {
    return {
      originalPrice: listedPrice,
      salePrice: null,
      effectivePrice: listedPrice,
      isOnSale: false,
      salePercent: 0,
    };
  }

  const originalPrice = explicitOriginal ?? listedPrice;
  let salePrice = null;

  if (explicitOriginal != null) {
    salePrice = listedPrice;
  } else if (product.salePrice != null) {
    salePrice = Number(product.salePrice);
  } else if (product.salePercent != null) {
    salePrice = roundPrice(originalPrice * (1 - Number(product.salePercent) / 100));
  }

  if (salePrice != null && salePrice < originalPrice) {
    const salePercent =
      product.salePercent != null
        ? Math.round(Number(product.salePercent))
        : originalPrice > 0
          ? Math.round((1 - salePrice / originalPrice) * 100)
          : 0;

    return {
      originalPrice,
      salePrice,
      effectivePrice: salePrice,
      isOnSale: true,
      salePercent,
    };
  }

  return {
    originalPrice,
    salePrice: null,
    effectivePrice: originalPrice,
    isOnSale: false,
    salePercent: 0,
  };
};

export const formatPrice = (amount) => {
  const value = Number(amount);
  if (Number.isNaN(value)) return "$0";
  const rounded = roundPrice(value);
  const cents = Math.round(Math.abs(rounded * 100)) % 100;
  if (cents === 0) return `$${Math.round(rounded)}`;
  return `$${rounded.toFixed(2)}`;
};

/** Normalize a product for cart storage with effective pricing applied. */
export const withEffectivePricing = (product) => {
  const pricing = getProductPricing(product);
  if (!pricing.isOnSale) return product;

  return {
    ...product,
    originalPrice: pricing.originalPrice,
    price: pricing.effectivePrice,
    isSale: true,
  };
};
